import React, { createContext, useContext, useEffect, useState } from "react";
import translations from "./translations";

const SUPPORTED_LANGUAGES = ["en", "zh", "es", "ru"];
const DEFAULT_LANGUAGE = "en";
const STORAGE_KEY = "ace_massage_language";

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    try {
      const savedLanguage = localStorage.getItem(STORAGE_KEY);

      if (SUPPORTED_LANGUAGES.includes(savedLanguage)) {
        return savedLanguage;
      }
    } catch (error) {
      console.warn("Unable to read saved language:", error);
    }

    return DEFAULT_LANGUAGE;
  });

  const setLanguage = (newLanguage) => {
    if (!SUPPORTED_LANGUAGES.includes(newLanguage)) {
      return;
    }

    setLanguageState(newLanguage);

    try {
      localStorage.setItem(STORAGE_KEY, newLanguage);
    } catch (error) {
      console.warn("Unable to save language:", error);
    }
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = (key, params = {}) => {
    const keys = key.split(".");

    let value = translations[language];

    for (const currentKey of keys) {
      value = value?.[currentKey];

      if (value === undefined) {
        return key;
      }
    }

    if (typeof value !== "string") {
      return value;
    }

    return value.replace(
      /\{(\w+)\}/g,
      (match, paramName) => {
        return params[paramName] !== undefined
          ? String(params[paramName])
          : match;
      }
    );
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        supportedLanguages: SUPPORTED_LANGUAGES,
        t,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("useLanguage must be used inside a LanguageProvider");
  }

  return context;
}